import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Hidden,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import PhoneIcon from '@material-ui/icons/Phone';
import logo from '../assets/logo-mark.png';
import siteConfig from '../siteConfig';
import { colors } from '../theme';
import { useHasReviews } from './FeedbacksList';

const allNavLinks = [
  { label: 'Services', href: '#services' },
  { label: 'Our Work', href: '#gallery' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' },
];

// Leaves out "Reviews" while that section is hidden.
export function useNavLinks() {
  const hasReviews = useHasReviews();
  return hasReviews ? allNavLinks : allNavLinks.filter((link) => link.href !== '#reviews');
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    transition: 'background-color 0.25s ease, box-shadow 0.25s ease, color 0.25s ease',
    backgroundColor: 'transparent',
    color: '#FFFFFF',
  },
  appBarSolid: {
    backgroundColor: '#FFFFFF',
    color: colors.navy,
    boxShadow: '0 2px 16px rgba(14,35,64,0.10)',
  },
  toolbar: {
    minHeight: 72,
    maxWidth: 1280,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2),
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    color: 'inherit',
    textDecoration: 'none',
    marginRight: 'auto',
    minWidth: 0,
  },
  logo: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  brandName: {
    fontFamily: theme.typography.h1.fontFamily,
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
    },
  },
  links: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  link: {
    color: 'inherit',
    fontFamily: theme.typography.body1.fontFamily,
    fontWeight: 600,
    fontSize: 15,
    whiteSpace: 'nowrap',
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    '&:hover': {
      backgroundColor: 'rgba(127,127,127,0.12)',
    },
  },
  drawerPaper: {
    width: 280,
    padding: theme.spacing(1, 0, 3),
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 1),
  },
  drawerLink: {
    fontFamily: theme.typography.h1.fontFamily,
    fontWeight: 700,
    fontSize: 18,
  },
  drawerCall: {
    margin: theme.spacing(3, 2, 0),
  },
}));

export default function NavBar() {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navLinks = useNavLinks();
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 40 });
  const solid = scrolled || drawerOpen;

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      className={`${classes.appBar} ${solid ? classes.appBarSolid : ''}`}
    >
      <Toolbar className={classes.toolbar} disableGutters>
        <a href="#top" className={classes.brand} aria-label={`${siteConfig.businessName} — back to top`}>
          <img src={logo} alt="" className={classes.logo} />
          <span className={classes.brandName}>
            <Hidden mdDown implementation="css">{siteConfig.businessName}</Hidden>
            <Hidden lgUp implementation="css">{siteConfig.shortName}</Hidden>
          </span>
        </a>

        <Hidden smDown implementation="css">
          <nav className={classes.links} aria-label="Main">
            {navLinks.map((link) => (
              <Button key={link.href} href={link.href} className={classes.link}>
                {link.label}
              </Button>
            ))}
          </nav>
        </Hidden>

        <Hidden xsDown implementation="css">
          <Button
            variant="contained"
            color="secondary"
            href={siteConfig.phoneHref}
            startIcon={<PhoneIcon />}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {siteConfig.phoneDisplay}
          </Button>
        </Hidden>

        <Hidden mdUp implementation="css">
          <IconButton
            color="inherit"
            edge="end"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            style={{ marginLeft: 8 }}
          >
            <MenuIcon />
          </IconButton>
        </Hidden>
      </Toolbar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerHeader}>
          <IconButton aria-label="Close menu" onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </div>
        <List component="nav" aria-label="Main">
          {navLinks.map((link) => (
            <ListItem button component="a" href={link.href} key={link.href} onClick={closeDrawer}>
              <ListItemText primary={link.label} classes={{ primary: classes.drawerLink }} />
            </ListItem>
          ))}
        </List>
        <Divider />
        <Button
          className={classes.drawerCall}
          variant="contained"
          color="secondary"
          size="large"
          href={siteConfig.phoneHref}
          startIcon={<PhoneIcon />}
        >
          Call {siteConfig.phoneDisplay}
        </Button>
      </Drawer>
    </AppBar>
  );
}
